/**
 * lastSaveAttemptStore — rolling FIFO breadcrumb of the most recent SL POST
 * mutation attempts, persisted to `localStorage` so DebugReport capture
 * can include them even when the browser console buffer has rotated away
 * the actionable lines (Rüdiger 2026-05-20 12:02 UTC incident — the
 * captured console had only 2 benign entries from 4s before the report,
 * because the hang event happened outside the buffer's retention window).
 *
 * Why a separate module instead of hooking the canonical SL client:
 *   - `serviceLayerClient.ts` is under carve-out for this PR (concurrent
 *     work in other worktrees). We wire breadcrumbs at the consumer layer
 *     (`useSubmitVisitReport`, `useSubmitCallResult`) instead.
 *
 * D-33 R1: the catch path MUST also record the attempt — silent failures
 * cannot hide here. Use `status: null` + `errorCode` ('NETWORK', 'TIMEOUT',
 * SL envelope code) when the request never completes.
 *
 * D-33 R3: no manufactured data. If a field is unknown, omit it (optional)
 * or set it to `null` (status). Do not invent placeholder values.
 *
 * Sortie-3 follow-up: a 30-second network-request rolling buffer (timestamps
 * + URLs + status, no payloads) was scoped alongside this breadcrumb but
 * deferred. Implementing it requires the Performance Resource Timing API
 * plus noise filtering — easily 80 LOC and a separate review. Track it in
 * the next sortie.
 */

const STORAGE_KEY = 'appic.lastSaveAttempts';
const MAX_ENTRIES = 5;

export interface SaveAttemptEntry {
  url: string;
  method: string;
  /** HTTP status when the request completed. `null` if the request threw before a response (network / timeout / abort). */
  status: number | null;
  /** ISO-8601 timestamp at the moment the attempt was recorded. */
  timestamp: string;
  /** SL request id, when available. */
  requestId?: string;
  /** SL envelope code (`code`) for non-2xx, or a synthetic code ('NETWORK', 'TIMEOUT') when the request threw. */
  errorCode?: string;
}

function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage ?? null;
  } catch {
    // SSR, sandboxed iframe, or `localStorage` access blocked.
    return null;
  }
}

function safeParse(raw: string | null): SaveAttemptEntry[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive: filter out any shape-incompatible entries written by older builds.
    return parsed.filter(
      (e): e is SaveAttemptEntry =>
        e != null
        && typeof e === 'object'
        && typeof (e as SaveAttemptEntry).url === 'string'
        && typeof (e as SaveAttemptEntry).method === 'string'
        && typeof (e as SaveAttemptEntry).timestamp === 'string'
        && ((e as SaveAttemptEntry).status === null || typeof (e as SaveAttemptEntry).status === 'number'),
    );
  } catch {
    return [];
  }
}

export function readSaveAttempts(): SaveAttemptEntry[] {
  const storage = getStorage();
  if (!storage) return [];
  try {
    return safeParse(storage.getItem(STORAGE_KEY));
  } catch {
    return [];
  }
}

export function recordSaveAttempt(entry: SaveAttemptEntry): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    const existing = safeParse(storage.getItem(STORAGE_KEY));
    existing.push(entry);
    // FIFO trim — drop oldest entries until under cap.
    while (existing.length > MAX_ENTRIES) existing.shift();
    storage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch {
    // Quota exceeded, private browsing throw, etc. — breadcrumbs are
    // best-effort. Surfacing here would derail the actual user mutation.
  }
}

export function clearSaveAttempts(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // noop — same rationale as `recordSaveAttempt`.
  }
}
